import React, { useMemo, useCallback, useState, useEffect } from "react";
import { useNavigate } from "react-router";
import isEmpty from "lodash/isEmpty";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import { Formik, Form } from "formik";
import Header from "../../../wizard/Header";
import Footer from "src/components/wizard/Footer/Footer";
import ChargeSection from "./ChargeSection";
import useMutation from "../../../../hooks/useMutation";
import useNotification from "../../../../hooks/notification";
import { UPDATE_SHIPMENT_MUTATION } from "../../../../constants/graphql/mutations";
import { getSectionsWithOneTimeCharges, buildInitialValues, validateCharges } from "./utils";
import EmptyAdditionalCharges from "./EmptyAdditionalCharges";
import RateCategoryPickerModal from "./RateCategoryPickerModal";
import RateMultiSelectModal from "./RateMultiSelectModal";
import OneTimeChargesModal from "src/components/project/inbound/OneTimeChargesModal";
import useReceivingWizardFlow from "../hooks/useReceivingWizardFlow";

import getTotalReceivingUnitsLabel from "src/components/capture/ReceivingWizard/Shipment/TotalReceivingUnits/getTotalReceivingUnitsLabel";
import { getShipmentValues } from "../../../../utils/shipment";
import WeightMismatchAlert from "../WeightMismatchAlert";

export default function AdditionalCharges({
  project,
  shipment,
  onClose,
  formikRef,
  actionsRef,
  verticalLayout = false,
}) {
  const navigate = useNavigate();
  const { notifyError } = useNotification();
  const { additionalChargesNextStep, totalSteps } = useReceivingWizardFlow(project);

  useEffect(() => {
    const warehouses = shipment?.warehousesFromRackLocations || [];
    const allWarehousesExcluded = warehouses.length > 0 && warehouses.every(w => w.excludeFromBilling);
    if (allWarehousesExcluded) {
      const params = new URLSearchParams(window.location.search);
      params.set("step", additionalChargesNextStep);
      navigate({ search: params.toString() }, { replace: true });
    }
  }, [shipment, navigate, additionalChargesNextStep]);
  const [updateShipment] = useMutation(UPDATE_SHIPMENT_MUTATION);
  const totalPieces = shipment?.totalPieces || [];
  const sections = useMemo(
    () => getSectionsWithOneTimeCharges(project, "inbound_enabled", shipment),
    [project, shipment]
  );
  const initialValues = useMemo(() => buildInitialValues(sections, shipment), [sections, shipment]);
  
  const [openCategoryPicker, setOpenCategoryPicker] = useState(false);
  const [openRatePicker, setOpenRatePicker] = useState(null);
  const [openOneTime, setOpenOneTime] = useState(false);
  const [hideOneTimeCharges, setHideOneTimeCharges] = useState(false);

  const handleBack = useCallback(() => {
    const pathname = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    params.set("step", "items");
    navigate({ pathname, search: params.toString() });
  }, [navigate]);

  const handleSubmit = useCallback(
    async (values, { setSubmitting }, { skipNavigation = false } = {}) => {
      let didSave = false;
      const inputPayload = {};
      Object.entries(values).forEach(([sectionKey, sectionValues]) => {
        const obj = {};
        Object.entries(sectionValues).forEach(([k, v]) => {
          // Include all numeric values, including 0 (for real-world use cases)
          // Only exclude empty strings or undefined values
          if (v !== "" && v !== undefined && v !== null) {
            obj[k] = Number(v);
          }
        });
        // Include the section if it has any defined values
        if (Object.keys(obj).length > 0) {
          // Convert oneTimeCharges to list format for consistency
          if (sectionKey === "oneTimeCharges") {
            inputPayload[sectionKey] = {
              list: Object.entries(obj).map(([notes, amount]) => ({ notes, amount })),
            };
          } else {
            inputPayload[sectionKey] = obj;
          }
        }
      });

      const hasActualCharges = Object.keys(inputPayload).some(key =>
        [
          "materialCharges",
          "laborCharges",
          "equipmentCharges",
          "openInspectCharges",
          "oneTimeCharges",
        ].includes(key)
      );

      // If there are actual charges and the shipment previously had "no additional charges",
      // we need to clear the no_additional_charges flag
      let finalPayload = { ...getShipmentValues(shipment), ...inputPayload };

      if (hasActualCharges && shipment?.oneTimeCharges?.no_additional_charges !== undefined) {
        // Clear the entire oneTimeCharges if we're adding other charges
        // This removes the no_additional_charges flag
        if (!inputPayload.oneTimeCharges) {
          finalPayload.oneTimeCharges = { list: [] };
        }
      }

      return updateShipment({
        variables: {
          id: shipment.id,
          input: finalPayload,
        },
        onSuccess: () => {
          didSave = true;
          if (skipNavigation) {
            setSubmitting(false);
            return true;
          }

          const pathname = window.location.pathname;
          const params = new URLSearchParams(window.location.search);
          params.set("step", additionalChargesNextStep);
          navigate({ pathname, search: params.toString() });
          setSubmitting(false);
          return true;
        },
        onFailure: errors => {
          notifyError(errors);
          setSubmitting(false);
          didSave = false;
          return false;
        },
      }).then(() => didSave);
    },
    [updateShipment, navigate, shipment, notifyError, additionalChargesNextStep]
  );

  const handleOpenAddCharges = () => {
    if (!shipment?.oneTimeCharges?.no_additional_charges) {
      setHideOneTimeCharges(false);
    }
    setOpenCategoryPicker(true);
  };

  const handleSelectCategory = categoryKey => {
    setOpenCategoryPicker(false);
    if (categoryKey === "oneTimeCharge") {
      setOpenOneTime(true);
    } else if (categoryKey === "noAdditionalCharges") {
      handleNoAdditionalCharges();
    } else {
      setOpenRatePicker({ categoryKey });
    }
  };

  const handleNoAdditionalCharges = useCallback(async () => {
    // Set no additional charges confirmation and auto-proceed
    const noChargesValues = {
      oneTimeCharges: { no_additional_charges: 0 },
    };

    await updateShipment({
      variables: {
        id: shipment?.id,
        input: { ...getShipmentValues(shipment), ...noChargesValues },
      },
      onSuccess: () => {
        // Auto-proceed to next step
        const pathname = window.location.pathname;
        const params = new URLSearchParams(window.location.search);
        params.set("step", additionalChargesNextStep);
        navigate({ pathname, search: params.toString() });
      },
      onFailure: errors => {
        notifyError(errors);
      },
    });
  }, [updateShipment, shipment, navigate, notifyError, additionalChargesNextStep]);

  const shipmentHasNoChargesConfirmed = shipment?.oneTimeCharges?.no_additional_charges !== undefined;
  const skipValidation = shipmentHasNoChargesConfirmed && !hideOneTimeCharges;

  if (actionsRef) {
    actionsRef.current = {
      saveProgress: () =>
        handleSubmit(formikRef.current?.values || {}, {
          setSubmitting: formikRef.current?.setSubmitting || (() => {}),
        }, {
          skipNavigation: true,
        }),
    };
  }

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validate={values => validateCharges(values, skipValidation)}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ isSubmitting, values, setValues }) => {
        // Validation: User must either add charges or confirm no charges
        const hasCharges =
          !isEmpty(values) && values.oneTimeCharges?.no_additional_charges === undefined;
        const hasNoChargesConfirmation = values.oneTimeCharges?.no_additional_charges !== undefined;
        // Also check if shipment already has no additional charges confirmed
        const shipmentHasNoChargesConfirmed =
          shipment?.oneTimeCharges?.no_additional_charges !== undefined;
        const isFormValid = hasCharges || hasNoChargesConfirmation || shipmentHasNoChargesConfirmed;

        return (
          <Form style={{ height: "100%" }}>
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
              minHeight="100%"
            >
              <Header title="Ancillary Charges" info={getTotalReceivingUnitsLabel(totalPieces)} />
              <WeightMismatchAlert shipment={shipment} />
              <Box px={2} mt={2} flexGrow={1} sx={{ overflowY: "auto", minHeight: 320 }}>
                {shipment?.oneTimeCharges?.no_additional_charges !== undefined &&
                !hideOneTimeCharges ? (
                  // Show confirmation when "no additional charges" is selected
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    minHeight={320}
                    gap={2}
                  >
                    <Typography variant="h6" color="success.main" align="center">
                      ✓ No Ancillary Charges Confirmed
                    </Typography>
                    <Typography variant="body2" color="text.secondary" align="center">
                      This shipment has been confirmed to have no ancillary charges.
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        // Set flag to hide one-time charges section and open picker
                        setHideOneTimeCharges(true);
                        setOpenCategoryPicker(true);
                      }}
                      size="small"
                    >
                      Add Charges Instead
                    </Button>
                  </Box>
                ) : isEmpty(values) ? (
                  <EmptyAdditionalCharges
                    description="Inbound shipments may include extra charges for materials, labor, or inspection services."
                    onAddBillingCharges={handleOpenAddCharges}
                  />
                ) : (
                  <>
                    {Object.entries(values)
                      .filter(([key]) => {
                        // Hide one-time charges section when hideOneTimeCharges is true
                        // But still show other sections
                        if (key === "oneTimeCharges" && hideOneTimeCharges) {
                          return false;
                        }
                        return true;
                      })
                      .map(([key, value]) => (
                        <ChargeSection
                          key={key}
                          section={{ key, entries: Object.entries(value) }}
                          timeIncrementMinutes={project?.settings?.time_increment_minutes}
                        />
                      ))}
                    <Box mt={2}>
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={handleOpenAddCharges}
                      >
                        Add More Charges
                      </Button>
                    </Box>
                  </>
                )}
              </Box>
              <Box mt={2}>
                <Footer
                  step={2}
                  steps={totalSteps}
                  startTime={verticalLayout ? null : shipment?.startTime}
                  completeTime={verticalLayout ? null : shipment?.completeTime}
                >
                  <div style={{ width: verticalLayout ? "100%" : "auto" }}>
                    {!verticalLayout && (
                      <Button
                        onClick={handleBack}
                        variant="outlined"
                        sx={{ mr: 1, textTransform: "none" }}
                      >
                        Back
                      </Button>
                    )}
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth={verticalLayout}
                      sx={{ textTransform: "none" }}
                      disabled={isSubmitting || !isFormValid}
                    >
                      {verticalLayout ? "Next Step" : "Next"}
                    </Button>
                  </div>
                </Footer>
              </Box>
            </Box>
            {openCategoryPicker && (
              <RateCategoryPickerModal
                onClose={() => setOpenCategoryPicker(false)}
                onSelect={handleSelectCategory}
                hasExistingCharges={!isEmpty(values)}
              />
            )}
            {openRatePicker && (
              <RateMultiSelectModal
                onClose={() => setOpenRatePicker(null)}
                categoryKey={openRatePicker.categoryKey}
                rates={project?.[openRatePicker.categoryKey] || {}}
                initialSelected={Object.keys(
                  {
                    materialRates: values?.materialCharges,
                    laborRates: values?.laborCharges,
                    openInspectRates: values?.openInspectCharges,
                    equipmentRates: values?.equipmentCharges,
                  }[openRatePicker.categoryKey] || {}
                )}
              />
            )}
            {openOneTime && (
              <OneTimeChargesModal
                shipment={shipment}
                onClose={() => setOpenOneTime(false)}
                onSubmit={submitted => {
                  const nextValues = {
                    ...values,
                    oneTimeCharges: {
                      ...(values?.oneTimeCharges || {}),
                      ...Object.fromEntries(submitted.list.map(v => [v.notes, v.amount])),
                    },
                  };
                  setValues(nextValues, false);
                  setOpenOneTime(false);
                }}
              />
            )}
          </Form>
        );
      }}
    </Formik>
  );
}
